import { Button } from "components/DesignSystem/Button/Button";
import { DateInput } from "components/DesignSystem/DateInput/DateInput";
import { MobileInput } from "components/DesignSystem/MobileInput/MobileInput";
import Modal from "components/DesignSystem/Modal/Modal";
import { SelectDropDown } from "components/DesignSystem/SelectDropDown/SelectDropDown";
import { TextInput } from "components/DesignSystem/TextInput/TextInput";
import { SelectAutofillCombobox } from "components/SelectAutofillCombobox";
import dayjs from "dayjs";
import { Field, FieldProps, Form, Formik, FormikValues } from "formik";
import { personaAutofill } from "formValidations/autofillSchema";
import { useGetCountriesQuery } from "store/apis/autofill";

export type PersonaModalProps = {
  open: boolean;
  isLoading: boolean;
  onClose: () => void;
  onSubmit: (v: FormikValues) => void;
  initialValues: any;
  entityId: string;
  customValidation?: any;
};

export const PersonaModal = ({
  isLoading,
  open,
  onClose,
  initialValues,
  onSubmit,
  entityId,
  customValidation,
}: PersonaModalProps) => {
  const { id } = initialValues;
  const localInitialValues = id
    ? initialValues
    : {
        title: "",
        first_name: "",
        last_name: "",
        email: "",
        address_id: null,
        citizenship: "",
        tax_id: "",
        ownership_stake: "",
      };

  const { data: countries = [] } = useGetCountriesQuery({});

  return (
    <Modal.Root onOpenChange={onClose} open={open}>
      {open && (
        <Formik
          onSubmit={onSubmit}
          initialValues={localInitialValues}
          validationSchema={customValidation || personaAutofill}
        >
          {({ values, submitForm, setFieldValue }) => {
            return (
              <Modal.Content asChild>
                <Form>
                  <Modal.Header>
                    <Modal.Title>{id ? "Edit" : "Add"} Persona</Modal.Title>
                    <Modal.Close />
                  </Modal.Header>
                  <Modal.Body className="t-flex t-flex-col t-gap-4">
                    <TextInput
                      block
                      label="First name"
                      name="first_name"
                      required
                    />
                    <TextInput
                      block
                      label="Last name"
                      name="last_name"
                      required
                    />
                    <TextInput block label="Email" name="email" />
                    <SelectAutofillCombobox
                      type="addresses"
                      entityId={entityId}
                      required={false}
                      name="address_id"
                      selected={values.address_id}
                      label="Address"
                      withForm
                      onChange={(autofillId) =>
                        setFieldValue("address_id", autofillId)
                      }
                      srollToSave
                    />
                    <SelectDropDown
                      name="citizenship"
                      label="Citizenship"
                      required
                    >
                      <option value="">Select Citizenship</option>
                      {countries?.map(
                        (country: {
                          iso_code: string;
                          name: string;
                          uuid: string;
                        }) => {
                          return (
                            <option key={country.uuid} value={country?.name}>
                              {country?.name}
                            </option>
                          );
                        }
                      )}
                    </SelectDropDown>
                    <TextInput
                      block
                      type="password"
                      label="Tax ID (e.g. SSN)"
                      name="tax_id"
                    />

                    <div className="t-flex t-flex-col t-gap-4">
                      <TextInput block label="Job title" name="job_title" />
                      <Field name="appointed_on">
                        {({ field }: FieldProps) => (
                          <DateInput
                            {...field}
                            block
                            label="Position start date"
                            portalId="appointed_on"
                          />
                        )}
                      </Field>
                      <div>
                        <MobileInput
                          customSize="regular"
                          name="phone_number"
                          label="Phone no"
                          placeholder="Enter number"
                        />
                      </div>
                      <Field name="date_of_birth">
                        {({ field }: FieldProps) => (
                          <DateInput
                            {...field}
                            block
                            label="Date of birth"
                            portalId="date_of_birth"
                          />
                        )}
                      </Field>

                      <TextInput
                        block
                        label="Government issued National Identification number"
                        name="national_identification_number"
                      />
                    </div>

                    <TextInput
                      block
                      label="Ownership stake"
                      name="ownership_stake"
                    />
                  </Modal.Body>
                  <Modal.FooterButtonGroup>
                    <Button
                      type="reset"
                      customType="secondary"
                      size="regular"
                      onClick={onClose}
                    >
                      Cancel
                    </Button>
                    <Button
                      type="button"
                      onClick={submitForm}
                      customType="primary"
                      size="regular"
                      isLoading={isLoading}
                      disabled={isLoading}
                    >
                      Save
                    </Button>
                  </Modal.FooterButtonGroup>
                </Form>
              </Modal.Content>
            );
          }}
        </Formik>
      )}
    </Modal.Root>
  );
};
